import { useState, useRef, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { useBiblesQuery } from '../hooks/queries';
import { useImportBible } from '../hooks/mutations';

interface BibleUploadProps {
  onSuccess?: () => void;
}

export default function BibleUpload({ onSuccess }: BibleUploadProps) {
  const { getAuthHeaders } = useUser();
  const { data: bibles = [] } = useBiblesQuery();
  const importBible = useImportBible();
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [fileSizeWarning, setFileSizeWarning] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleProgress = (event: Event) => {
      setUploadProgress((event as CustomEvent).detail.progress);
    };

    window.addEventListener('bibleUploadProgress', handleProgress);
    return () => window.removeEventListener('bibleUploadProgress', handleProgress);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setErrorMessage('');
    setSuccessMessage('');
    setFileSizeWarning('');
    setDuplicateWarning('');

    const maxWarningSize = 5 * 1024 * 1024;
    if (selectedFile.size > maxWarningSize) {
      setFileSizeWarning(`File is ${(selectedFile.size / 1024 / 1024).toFixed(1)}MB. Upload may take longer.`);
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const xmlContent = event.target?.result as string;
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

        if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
          setErrorMessage('Invalid XML file. Please check the file format.');
          setFile(null);
          return;
        }

        const root = xmlDoc.documentElement;
        const translationName = root.getAttribute('name') || '';

        if (translationName) {
          const isDuplicate = bibles.some(t => t.name === translationName);
          if (isDuplicate) {
            setDuplicateWarning(`A Bible translation named "${translationName}" already exists. Uploading will replace the existing one.`);
          }
        }
      } catch (err) {
        console.error('Error reading XML file:', err);
      }
    };
    reader.readAsText(selectedFile);

    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploadProgress(0);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const authHeaders = getAuthHeaders() as Record<string, string>;
      await importBible.mutateAsync({ file, authHeaders });

      setSuccessMessage('Bible uploaded successfully');

      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (error) {
      setErrorMessage((error as Error).message);

      setTimeout(() => {
        setErrorMessage('');
      }, 2000);
    } finally {
      setUploadProgress(0);
    }
  };

  const handleClear = () => {
    setFile(null);
    setFileSizeWarning('');
    setDuplicateWarning('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploading = importBible.isPending;

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xml"
        onChange={handleFileChange}
        className="hidden"
        disabled={uploading}
      />

      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full px-4 py-2 bg-bible text-white rounded-lg hover:bg-bible-dark transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
      >
        {file ? `Selected: ${file.name}` : 'Select XML File'}
      </button>

      {fileSizeWarning && (
        <div className="p-3 bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg">
          {fileSizeWarning}
        </div>
      )}

      {duplicateWarning && (
        <div className="p-3 bg-orange-50 border border-orange-200 text-orange-700 text-sm rounded-lg">
          {duplicateWarning}
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!file || uploading}
        className="w-full px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
      >
        {uploading ? `Uploading... ${uploadProgress}%` : 'Upload'}
      </button>

      {uploading && (
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className="bg-amber-600 h-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
          {errorMessage}
        </div>
      )}

      {file && !uploading && (
        <button
          onClick={handleClear}
          className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
        >
          Clear Selection
        </button>
      )}
    </div>
  );
}
